import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { login, fetchMe } from "@/lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMe()
      .then(() => {
        if (alive) nav("/admin", { replace: true });
      })
      .catch(() => { /* not logged in — stay on page */ });
    return () => { alive = false; };
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Inloggad!");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Inloggning misslyckades");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-5 py-10"
      style={{ background: "linear-gradient(180deg, #0B132B 0%, #1a1035 45%, #6b0e18 100%)" }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
            <span className="w-8 h-[2px] bg-[#FFB3B3]" /> Admin
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white mt-3">
            Logga in
          </h1>
          <p className="font-body text-white/70 mt-2 text-sm">
            Swedcro Måleri &amp; Fasad — administratör
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white/[0.04] border-2 border-white/20 p-6 md:p-8 space-y-5 backdrop-blur"
          data-testid="admin-login-form"
        >
          <label className="block">
            <span className="font-display font-bold text-xs uppercase tracking-[0.15em] text-white/80 flex items-center gap-2 mb-2">
              <Mail size={14} /> E-post
            </span>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-2 border-white/30 focus:border-[#D92525] outline-none px-4 py-3 font-body text-white placeholder:text-white/40 transition-colors"
              placeholder="info@swedcro.se"
            />
          </label>

          <label className="block">
            <span className="font-display font-bold text-xs uppercase tracking-[0.15em] text-white/80 flex items-center gap-2 mb-2">
              <Lock size={14} /> Lösenord
            </span>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-2 border-white/30 focus:border-[#D92525] outline-none px-4 py-3 font-body text-white placeholder:text-white/40 transition-colors"
              placeholder="••••••••"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#D92525] text-white font-display font-black text-sm tracking-[0.15em] uppercase px-6 py-4 flex items-center justify-center gap-3 hover:bg-white hover:text-[#0B132B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed border-2 border-[#D92525] hover:border-white"
          >
            {loading ? "Loggar in..." : "Logga in"}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>

        <div className="text-center mt-6">
          <a
            href="/"
            data-testid="admin-login-back"
            className="font-body text-sm text-white/60 hover:text-white transition-colors"
          >
            ← Tillbaka till sajten
          </a>
        </div>
      </div>
    </div>
  );
}
